import { ChangeDetectionStrategy, Component, computed, input } from '@angular/core';
import { ChartData, ChartDataset, ChartOptions, Chart, registerables } from 'chart.js';
import { ChartUtils } from '../../utils';
import { BaseChartDirective } from 'ng2-charts';

Chart.register(...registerables);

@Component({
    selector: 'ca-bar-chart',
    templateUrl: './bar-chart.component.html',
    styleUrls: ['./bar-chart.component.scss'],
    changeDetection: ChangeDetectionStrategy.OnPush,
    imports: [BaseChartDirective]
})
export class BarChartComponent {
    labels = input.required<string[]>();
    tooltipLabel = input.required<string>();
    datasets = input.required<ChartDataset<'bar'>[]>();
    locale = input.required<string>();
    valueUnit = input<string | null>(null);
    indexAxis = input<'y' | 'x' | undefined>('y');
    ticksCallback = input<(value: string | number) => string>();

    barChartData = computed<ChartData<'bar'>>(() => ({
        labels: this.labels(),
        datasets: this.datasets()!
    }));

    barChartOptions = computed<ChartOptions<'bar'>>(() => {
        // eslint-disable-next-line @typescript-eslint/no-this-alias
        const self = this;
        return {
            indexAxis: this.indexAxis(),
            responsive: true,
            maintainAspectRatio: false,
            scales: {
                x: {
                    min: 10000,
                    beginAtZero: true,
                    grid: {
                        display: true,
                        drawTicks: false
                    },
                    ticks: {
                        ...ChartUtils.ticks,
                        callback: value => {
                            if (this.ticksCallback() !== undefined) {
                                return this.ticksCallback()!(value);
                            }
                            if (typeof value === 'number') {
                                value = value.toLocaleString(this.locale());
                            }
                            return this.valueUnit() ? `${value} ${this.valueUnit()}` : value;
                        }
                    }
                },
                y: {
                    offset: true,
                    stacked: true,
                    ticks: {
                        ...ChartUtils.ticks,
                        callback: (_, index: number) => {
                            const label = this.labels()[index];
                            return ChartUtils.splitWideLabelToTwoLinesIfPossible(label);
                        }
                    },
                    grid: {
                        display: false
                    }
                }
            },
            plugins: {
                legend: {
                    position: 'bottom',
                    labels: ChartUtils.labels
                },
                tooltip: {
                    ...ChartUtils.tooltip,
                    callbacks: {
                        title: tooltipItems => {
                            return tooltipItems[0].label || '';
                        },
                        label: () => {
                            return '';
                        },
                        footer: function (tooltipItems) {
                            // function needed to have access to chart `this` context
                            const chart = this.chart;
                            const idx = tooltipItems[0].dataIndex;
                            let sum = 0;

                            chart.data.datasets.forEach(dataset => {
                                const value = dataset.data[idx];
                                if (typeof value === 'number') sum += value;
                                else if (value && typeof value === 'object' && 'x' in value) sum += Number((value as { x: unknown }).x);
                                else if (value && typeof value === 'object' && 'y' in value) sum += Number((value as { y: unknown }).y);
                            });
                            const unit = self.valueUnit() ? ` ${self.valueUnit()}` : '';
                            return `${self.tooltipLabel()}: ${sum.toLocaleString(self.locale())}${unit}`;
                        }
                    }
                }
            }
        };
    });
}
